import LivenessSessionStatus from "../domain/LivenessSessionStatus.js"
import type { DisplayText } from "./dto/DisplayText.js"

/**
 * @description
 *	Dictionary for en-US locale.
 */
const enUS: DisplayText = {
	a11yVideoLabelText: "Webcam for liveness check",
	cameraMinSpecificationsHeadingText: "Camera does not meet minimum specifications",
	cameraMinSpecificationsMessageText: "Camera must support at least 320*240 resolution and 15 frames per second.",
	cameraNotFoundHeadingText: "Camera is not accessible.",
	cameraNotFoundMessageText:
		"Check that a camera is connected and there is not another application using the camera. You may have to go into settings to grant camera permissions and close out all instances of your browser and retry.",
	cancelLivenessCheckText: "Cancel Liveness check",
	clientHeaderText: "Client error",
	clientMessageText: "Check failed due to client issue",
	connectionTimeoutHeaderText: "Connection time out",
	connectionTimeoutMessageText: "Connection has timed out.",
	errorLabelText: "Error",
	faceDistanceHeaderText: "Forward movement detected",
	faceDistanceMessageText: "Avoid moving closer when connecting.",
	goodFitAltText: "Ilustration of a person's face, perfectly fitting inside of an oval.",
	goodFitCaptionText: "Good fit",
	hintCanNotIdentifyText: "Move face in front of camera",
	hintCenterFaceInstructionText:
		"Instruction: Before starting the check, make sure your camera is at the center top of your screen and center your face to the camera. When the check starts an oval will show up in the center. You will be prompted to move forward into the oval and then prompted to hold still. After holding still for a few seconds, you should hear check complete.",
	hintCenterFaceText: "Center your face",
	hintCheckCompleteText: "Check complete",
	hintConnectingText: "Connecting...",
	hintFaceDetectedText: "Face detected",
	hintFaceOffCenterText: "Face is not in the oval, center your face to the camera.",
	hintHoldFaceForFreshnessText: "Hold still",
	hintIlluminationNormalText: "Lighting conditions normal",
	hintIlluminationTooBrightText: "Move to dimmer area",
	hintIlluminationTooDarkText: "Move to brighter area",
	hintMatchIndicatorText: "50% completed. Keep moving closer.",
	hintMoveFaceFrontOfCameraText: "Move face in front of camera",
	hintTooCloseText: "Move back",
	hintTooFarText: "Move closer",
	hintTooManyFacesText: "Ensure only one face is in front of camera",
	hintVerifyingText: "Verifying...",
	landscapeHeaderText: "Landscape orientation not supported",
	landscapeMessageText: "Rotate your device to portrait (vertical) orientation.",
	multipleFacesHeaderText: "Multiple faces detected",
	multipleFacesMessageText: "Ensure only one face is present in front of the camera when connecting.",
	photosensitivityWarningBodyText: "This check flashes different colors. Use caution if you are photosensitive.",
	photosensitivityWarningHeadingText: "Photosensitivity warning",
	photosensitivityWarningInfoText:
		"Some people may experience epileptic seizures when exposed to colored lights. Use caution if you, or anyone in your family, have an epileptic condition.",
	photosensitivityWarningLabelText: "More information about photosensitivity",
	photosensitivyWarningBodyText: "This check flashes different colors. Use caution if you are photosensitive.",
	photosensitivyWarningHeadingText: "Photosensitivity warning",
	photosensitivyWarningInfoText:
		"Some people may experience epileptic seizures when exposed to colored lights. Use caution if you, or anyone in your family, have an epileptic condition.",
	photosensitivyWarningLabelText: "More information about photosensitivity",
	portraitMessageText: "Ensure your device remains in portrait (vertical) orientation for the check's duration.",
	recordingIndicatorText: "REC",
	retryCameraPermissionsText: "Retry",
	serverHeaderText: "Server issue",
	serverMessageText: "Cannot complete check due to server issue",
	startScreenBeginCheckText: "Start video check",
	timeoutHeaderText: "Time out",
	timeoutMessageText:
		"Face didn't fit inside oval in time limit. Try again and completely fill the oval with face in it.",
	tooFarAltText:
		"Illustration of a person's face inside of an oval; there is a gap between the perimeter of the face and the boundaries of the oval.",
	tooFarCaptionText: "Too far",
	tryAgainText: "Try again",
	waitingCameraPermissionText: "Waiting for you to allow camera permission.",

	// Biometry display text.
	canCloseAfterCompletion: "You can close this page.",
	faceLivenessDetectionCompletion: {
		[LivenessSessionStatus.EXPIRED]: "The session has expired; you will need to try again.",
		[LivenessSessionStatus.FAILED]: "The liveness detection failed; you will need to try again.",
		[LivenessSessionStatus.SUCCEEDED]: "We are analyzing your liveness proof."
	},
	faceSignatureCompletion: {
		[LivenessSessionStatus.EXPIRED]: "The session has expired; you will need to try again.",
		[LivenessSessionStatus.FAILED]: "The face signature failed; you will need to try again.",
		[LivenessSessionStatus.SUCCEEDED]: "We are analyzing your face signature."
	},
	landingProvidedBy: "Verification provided by",
	landingStartButton: "Start verification",
	landingStepCamera: "Allow camera access",
	landingStepHold: "Hold still for a few seconds",
	landingStepOval: "Fit your face in the oval",
	landingSubtitle: "We confirm it's you with a liveness check. It takes less than a minute.",
	landingTitle: "Identity verification",
	photosensitiveWarningHeading: "Photosensitivity Warning",
	photosensitiveWarningText:
		"This verification displays colored lights and flashes. Be cautious if you are photosensitive. ",
	photosensitiveWarningInfo:
		"Some people may experience epileptic seizures when exposed to colored lights and flashes. Be cautious if you, or someone in your family, suffers from epilepsy.",
	exitBtnText: "Back",
	errorStates: {
		RUNTIME_ERROR: "ERROR",
		SERVER_ERROR: "ERROR",
		CAMERA_ACCESS_ERROR: "Camera Access Error",
		CAMERA_FRAMERATE_ERROR: "Camera Framerate Error",
		FACE_DISTANCE_ERROR: "Face Distance Error",
		MOBILE_LANDSCAPE_ERROR: "Mobile Landscape Error",
		TIMEOUT: "Timeout",
		UNKNOWN_ERROR: "ERROR"
	},
	errorMessages: {
		ValidationException: "Invalid session. Please create a new one.",
		TIMEOUT: "Session timeout. Please create a new one.",
		default: "Error. Please create a new session."
	}
}

export default enUS
